/**
 * Submit a lead, and do not lose it if the API is down (SP-400). Every jobsite
 * form funnels through here so the fallback is a property of submitting rather
 * than a rule each new form must remember. During a real outage the browser
 * usually sees a rejected promise, not a status: Cloudflare's 502 page carries
 * no CORS headers and the API's custom-domain check fails closed. So `catch` is
 * the primary detector and `!res.ok` the secondary one; both are load-bearing.
 */

export type SubmitLeadStatus = "ok" | "backed-up" | "failed";

export interface SubmitLeadResult {
  status: SubmitLeadStatus;
  /** Absent on a clean primary submit. */
  reason?: BackupReason;
  /** The API's own words on a 4xx, for callers that show them (WebMCP). */
  errorMessage?: string;
}

export type BackupReason = "primary_5xx" | "primary_network" | "primary_timeout";

export interface SubmitLeadOptions {
  apiUrl: string;
  siteId: string;
  /** Named in the outage alert, so Slack says who lost a lead rather than a uuid. */
  businessName?: string;
  /** Absent simply disables the fallback. */
  backupApiUrl?: string;
  payload: Record<string, unknown>;
  /** CF answers 502 fast, but an origin mid-restart can hang instead. */
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
}

const DEFAULT_TIMEOUT_MS = 8000;

/** A DOMException from AbortSignal.timeout, distinguished from a network drop. */
function isTimeout(err: unknown): boolean {
  return (
    typeof err === "object" && err !== null && (err as { name?: string }).name === "TimeoutError"
  );
}

/** What the API said about a 4xx, when it said anything parseable. */
async function rejectionMessage(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { message?: unknown; error?: unknown };
    const text = typeof body.message === "string" ? body.message : body.error;
    return typeof text === "string" && text.trim() ? text : undefined;
  } catch {
    return undefined;
  }
}

function timeoutSignal(ms: number): AbortSignal | undefined {
  // Safari < 16 has neither; the request then runs without a deadline.
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  return undefined;
}

export async function submitLead(options: SubmitLeadOptions): Promise<SubmitLeadResult> {
  const {
    apiUrl,
    siteId,
    backupApiUrl,
    businessName,
    payload,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = fetch,
  } = options;

  let reason: BackupReason;

  try {
    const res = await fetchImpl(`${apiUrl}/api/sites/${siteId}/forms/contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: timeoutSignal(timeoutMs),
    });

    if (res.ok) {
      return { status: "ok" };
    }

    // A 4xx is a decision, not an outage; backing it up would bypass the defense just applied.
    if (res.status < 500) {
      return { status: "failed", errorMessage: await rejectionMessage(res) };
    }

    reason = "primary_5xx";
  } catch (err) {
    reason = isTimeout(err) ? "primary_timeout" : "primary_network";
  }

  if (!backupApiUrl) {
    return { status: "failed", reason };
  }

  // Single-use and already spent; parking a bot-protection credential buys nothing.
  const { turnstileToken: _spent, ...forBackup } = payload;

  try {
    const res = await fetchImpl(`${backupApiUrl}/capture/${siteId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...forBackup,
        __backupReason: reason,
        ...(businessName ? { __businessName: businessName } : {}),
      }),
      signal: timeoutSignal(timeoutMs),
    });

    // The backup answers 503 when its own write fails, so non-ok means the lead is nowhere.
    return res.ok ? { status: "backed-up", reason } : { status: "failed", reason };
  } catch {
    return { status: "failed", reason };
  }
}
